import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, PencilLine, ExternalLink, Radio } from "lucide-react";
import { InterviewRoadmap } from "./interview-roadmap";
import { useInterviews, useContinueInterview } from "@/hooks/queries/use-interviews";
import { useEpicRequest } from "@/hooks/queries/use-epic-requests";
import { Button } from "@/components/ui/button";
import type { Job } from "@/lib/api/mailbox";
import type { Interview } from "@/lib/api/interviews";
import { useIdeaHref } from "@/hooks/use-new-look";

/** Best-effort extraction of an epicRequestId from a (possibly terminal) job's result. */
export function jobResultEpicRequestId(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const value = (result as Record<string, unknown>).epicRequestId;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Stale-job recovery (bug #4). Given the caller's interviews, the job currently
 * being viewed, and that job's result, returns the jobId of the interview's LIVE
 * session when the viewed job has been superseded — or null when the viewed job
 * is itself an interview's current job (not stale) or no successor is found.
 */
export function findSupersedingJobId(
  interviews: readonly Pick<Interview, "jobId" | "epicRequestId">[],
  currentJobId: string,
  jobResult: unknown,
): string | null {
  // The viewed job is some interview's current job → not superseded.
  if (interviews.some((i) => i.jobId === currentJobId)) return null;
  const erId = jobResultEpicRequestId(jobResult);
  if (!erId) return null;
  const moved = interviews.find((i) => i.epicRequestId === erId);
  return moved?.jobId && moved.jobId !== currentJobId ? moved.jobId : null;
}

/**
 * InterviewFlowPanel (ENG-144 / ENG-148) — the deeper-interview affordances shown
 * on the mailbox job page for an `epic_request` job:
 *
 *  - ROADMAP: always renders the {@link InterviewRoadmap} (from the shared
 *    INTERVIEW_DEFINITION) so the user sees the full depth while they answer.
 *  - CONTINUE (ENG-148): once the linked interview is `completed` AND its epic
 *    request is still `pending`, offers a real "Continue interview" action
 *    (POST /interviews/:id/continue). Continuing dispatches a NEW seeded job
 *    (jobB) that carries the full prior transcript, re-points the interview at
 *    it, and navigates to jobB so the user can add more detail. The completion
 *    hook (ENG-142) then re-snapshots cumulatively and the daemon (ENG-149)
 *    UPDATES the SAME epic request in place — no duplicate. No turns are written
 *    by this component.
 *
 * The linked interview is located by correlating the job id against the caller's
 * interview list (there is no get-by-job endpoint); this is a read-only lookup
 * for the UI affordance, not a source of truth for turns.
 */
export function InterviewFlowPanel({
  job,
}: {
  job: Pick<Job, "id" | "type" | "status" | "result">;
}) {
  const ideaHref = useIdeaHref();
  const isEpicRequest = job.type === "epic_request";
  const navigate = useNavigate();

  const { data } = useInterviews({}, { enabled: isEpicRequest });
  const interview = useMemo(
    () => data?.data.find((item) => item.jobId === job.id) ?? null,
    [data, job.id]
  );

  // Stale-job recovery (bug #4): if THIS page's job is not the current job of any
  // interview, but it produced an epic request whose interview has since moved to
  // a NEWER job (e.g. a continuation), the user is looking at a superseded
  // session. Detect that and offer a link to the live one — otherwise a working
  // continuation looks dead because the browser is parked on an old job.
  const supersededByJobId = useMemo(
    () => (isEpicRequest ? findSupersedingJobId(data?.data ?? [], job.id, job.result) : null),
    [isEpicRequest, job.id, job.result, data]
  );

  // The Continue action is gated on the LINKED ER still being `pending`, so we
  // need the ER's status. Enabled only once the interview carries an
  // epicRequestId; on the mailbox job page the viewer is the owner.
  const { data: epicRequest } = useEpicRequest(interview?.epicRequestId ?? "");

  const continueInterview = useContinueInterview();

  // Only render for the interview job type — execution jobs reuse this page too.
  if (!isEpicRequest) return null;

  // Continue is offered ONLY when the interview is completed AND the ER is
  // pending (approved/converted requests are content-locked, so continuing is
  // pointless — mirrors the server-side gate in continueInterview).
  const canContinue =
    interview?.status === "completed" && epicRequest?.status === "pending";

  const handleContinue = async () => {
    if (!interview || !canContinue) return;
    try {
      const result = await continueInterview.mutateAsync(interview.id);
      void navigate(`/mailbox/jobs/${result.jobId}`);
    } catch {
      // Failure is surfaced via the mutation state / global error handling;
      // nothing to write here.
    }
  };

  return (
    <div className="glass-card rounded-2xl mb-4 p-4 md:p-6 space-y-4">
      {/* Stale-job recovery banner (bug #4): this session was superseded by a
          newer one (e.g. a continuation). Point the user at the live job. */}
      {supersededByJobId && (
        <div className="rounded-md border border-amber-800/30 bg-amber-900/[0.06] p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
            <Radio className="h-4 w-4" />
            This interview continued in a newer session
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            You&apos;re viewing an earlier session for this request. The live
            interview has moved to a newer one.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => { void navigate(`/mailbox/jobs/${supersededByJobId}`); }}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Open the current session
          </Button>
        </div>
      )}

      {/* Reverse link (ENG-147): once the interview is linked to its created
          epic request (Interview.epicRequestId set on completion), offer a
          link back to the ER — closing the loop with the ER → interview link
          on the epic-request detail page. */}
      {interview?.epicRequestId && (
        <Link
          to={ideaHref(interview.epicRequestId)}
          className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5 text-purple-300" />
          <span className="font-medium">View epic request</span>
        </Link>
      )}

      <InterviewRoadmap />

      {canContinue && (
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <PencilLine className="h-4 w-4 text-purple-300" />
            Want to add more detail?
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Continue this interview to enrich the request. A fresh conversation
            starts on your machine carrying everything you already said, and the
            SAME epic request is updated in place — nothing is lost or duplicated.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => { void handleContinue(); }}
            disabled={continueInterview.isPending}
          >
            {continueInterview.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-2" />
            )}
            Continue interview
          </Button>
        </div>
      )}
    </div>
  );
}
